import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SOCIALS, WHATSAPP, SITE } from "@/lib/site";
import { useContent } from "@/hooks/useData";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle } from "lucide-react";

export default function Contact() {
  const { content } = useContent();
  const c = content.contact_page || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, type: "contact" });
      toast.success(data.message || "Thank you. We'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Contact" title="Send your floor" accent="plan." description="Tell us about your project — floor plan, product list, style references and budget. Our team replies within one business day." />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <form onSubmit={submit} className="lg:col-span-7 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="label-eyebrow text-[#5E5A54]">Name</Label>
                <Input required value={form.name} onChange={set("name")} className="rounded-none bg-white border-[#ECEAE4] h-12" data-testid="contact-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow text-[#5E5A54]">Phone</Label>
                <Input value={form.phone} onChange={set("phone")} className="rounded-none bg-white border-[#ECEAE4] h-12" data-testid="contact-phone" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Email</Label>
              <Input required type="email" value={form.email} onChange={set("email")} className="rounded-none bg-white border-[#ECEAE4] h-12" data-testid="contact-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Your project</Label>
              <Textarea required value={form.message} onChange={set("message")} rows={6} className="rounded-none bg-white border-[#ECEAE4]" placeholder="Describe your project, rooms, products and timeline." data-testid="contact-message" />
            </div>
            <Button type="submit" disabled={loading} className="rounded-none bg-[#2C2A29] hover:bg-[#4A4641] text-[#FFFFFF] h-12 px-10 label-eyebrow text-[10px]" data-testid="contact-submit">
              {loading ? "Sending…" : "Send Enquiry"}
            </Button>
          </form>

          <div className="lg:col-span-4 lg:col-start-9 space-y-8">
            <div className="flex gap-4">
              <MapPin className="text-[#9E8773] shrink-0" size={22} strokeWidth={1.5} />
              <div>
                <p className="label-eyebrow text-[10px] text-[#9E8773]">Based In</p>
                <p className="text-[#2C2A29] font-light mt-1">{c.based_in || SITE.base}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Mail className="text-[#9E8773] shrink-0" size={22} strokeWidth={1.5} />
              <div>
                <p className="label-eyebrow text-[10px] text-[#9E8773]">Email</p>
                <p className="text-[#2C2A29] font-light mt-1">{c.email || SITE.email}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <MessageCircle className="text-[#9E8773] shrink-0" size={22} strokeWidth={1.5} />
              <div>
                <p className="label-eyebrow text-[10px] text-[#9E8773]">WhatsApp</p>
                <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="text-[#2C2A29] font-light mt-1 block hover:text-[#9E8773]" data-testid="contact-whatsapp">{c.whatsapp_label || "Chat with our team →"}</a>
              </div>
            </div>
            <div className="pt-6 border-t border-[#ECEAE4]">
              <p className="label-eyebrow text-[10px] text-[#9E8773] mb-3">Follow</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {SOCIALS.map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="text-[#5E5A54] font-light hover:text-[#2C2A29] transition-colors" data-testid={`contact-social-${s.label.toLowerCase()}`}>
                    {s.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
